import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import {
  differenceInYears,
  format,
  formatDate,
  isValid,
  parse,
} from 'date-fns';
import { SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import {
  CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY,
  DISTRICTS,
} from '../../../../utils/constants.js';
import { formatDateToISO } from '../../../../utils/format.js';
import { REGEX_DATE_EU } from '../../../../utils/regex.js';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../..//ui/form.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../..//ui/select.jsx';
import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import { DateInputWithDatePicker } from '../../../ui/date-picker.jsx';
import FormFooter from '../../../ui/form-footer.jsx';
import { Input } from '../../../ui/input.jsx';

const FormCustomerInfo = ({ onCancel, onSuccess }) => {
  const { user } = useSnapshot(authState);

  const formUpdateCustomerInfo = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      cc: undefined,
      birthDate: undefined,
      phone: '',
      location: undefined,
      address: undefined,
      district: undefined,
      zipCode: undefined,
      consentTermsOfParticipation: false,
      consentDataSharing: false,
      consentDataSharingLivliFamily: false,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Apelido tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        cc: yup
          .string()
          .min(2)
          .required('Nº Identificação é um campo obrigatório'),
        birthDate: yup
          .string()
          .required('Data de Nascimento é um campo obrigatório')
          .matches(
            REGEX_DATE_EU,
            'Data de Nascimento possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'dob',
            'Apenas clientes com 18 anos ou mais podem participar na campanha',
            function (value) {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());

              return differenceInYears(new Date(), parsedDate) >= 18;
            },
          ),
        phone: yup.string().when({
          is: (value) => value,
          then: (schema) => {
            return schema.matches(
              /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/,
              'Nº de Telemóvel inválido',
            );
          },
          otherwise: (schema) => schema.optional(),
        }),
        consentTermsOfParticipation: yup
          .boolean(true)
          .required('Condições de participação é um campo obrigatório')
          .oneOf([true], 'Condições de participação necessita de ser aceite'),
      }),
    ),
  });

  const updateCustomerMutation = useMutation({
    mutationFn: async (data) => {
      const updateCustomer = await msCatalystApi.put(`/auth/customers`, {
        ...data,
        fullName: `${data.firstName} ${data.lastName}`,
      });

      return updateCustomer.data;
    },
  });

  const onFormSubmit = async (data) => {
    try {
      const newData = { ...data };
      newData.birthDate = formatDateToISO(data.birthDate);

      await updateCustomerMutation.mutateAsync(newData);

      toast.success('Conta atualizada com sucesso!');

      authState.user = {
        ...authState.user,
        details: {
          ...authState.user.details,
          ...newData,
        },
      };

      onSuccess();
    } catch (err) {
      const message = err?.response?.data?.message;

      if (message?.includes('Duplicate value for cc')) {
        toast.error('Já existe uma conta com este Nº Identificação');
      } else {
        toast.error('Erro ao atualizar conta. Tente novamente mais tarde');
      }
    }
  };

  useEffect(() => {
    if (user?.details) {
      formUpdateCustomerInfo.reset({
        ...user.details,
        birthDate: user.details.birthDate
          ? format(user.details.birthDate, 'dd-MM-yyyy')
          : undefined,
        consentTermsOfParticipation: false,
        consentDataSharing: false,
        consentDataSharingLivliFamily: false,
      });
    }
  }, []);

  return (
    <Form {...formUpdateCustomerInfo}>
      <form onSubmit={formUpdateCustomerInfo.handleSubmit(onFormSubmit)}>
        {/* fields */}
        <div className="grid gap-x-10 gap-y-4 lg:grid-cols-2">
          <FormField
            control={formUpdateCustomerInfo.control}
            name="firstName"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Nome</FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="lastName"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Apelido</FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="cc"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>
                  Nº de documento comprovativo de identidade
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>

                <FormDescription>
                  (Bilhete de Identidade, Passaporte, Carta de Condução ou
                  Cartão de Residência)
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="birthDate"
            render={({ field }) => {
              let selectedDate = null;

              if (field?.value?.match(REGEX_DATE_EU)) {
                const [day, month, year] = field.value.split('-');
                selectedDate = new Date(`${year}-${month}-${day}`);
              }

              return (
                <FormItem variant="portal">
                  <FormLabel required={true}>Data de nascimento</FormLabel>
                  <FormControl>
                    <DateInputWithDatePicker
                      input={{
                        ...field,
                        variant: 'portal',
                        onChange: (e) => {
                          const date = e.target.value;

                          if (!date && !isValid(new Date(date))) return;

                          field.onChange(date);
                        },
                      }}
                      calendar={{
                        selected: selectedDate,
                        onSelect: (date) => {
                          if (!date && !isValid(new Date(date))) return;

                          field.onChange(
                            formatDate(new Date(date), 'dd-MM-yyyy'),
                          );
                        },
                      }}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="phone"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Nº Telemóvel</FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="location"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Localidade</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="address"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Morada</FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="district"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Distrito</FormLabel>
                <Select
                  disabled={updateCustomerMutation.isPending}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {DISTRICTS.map((item, i) => {
                      return (
                        <SelectItem key={i} value={item}>
                          {item}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Código postal</FormLabel>
                <FormControl>
                  <Input
                    disabled={updateCustomerMutation.isPending}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Consentimentos */}
        <div className="mt-10 flex flex-col gap-4 ">
          <FormField
            control={formUpdateCustomerInfo.control}
            name="consentTermsOfParticipation"
            render={({ field }) => (
              <FormItem variant="portal" className="col-span-1 lg:col-span-2">
                <FormLabel
                  className="!font-lively !text-base font-bold"
                  required={true}
                >
                  Condições de participação
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5 md:h-10">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentTermsOfParticipation"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    Sim, autorizo que os dados acima sejam utilizados pelo MAR
                    SHOPPING para efeitos da minha participação em campanhas
                    realizadas pelo MAR SHOPPING.
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="consentDataSharing"
            render={({ field }) => (
              <FormItem variant="portal" className="col-span-1 lg:col-span-2">
                <FormLabel className="!font-lively !text-base font-bold">
                  Comunicações de Marketing
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5 md:h-10">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentDataSharing"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    Sim, autorizo que os dados acima sejam utilizados pelo MAR
                    SHOPPING e seus parceiros no âmbito de ações informativas e
                    comerciais.
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formUpdateCustomerInfo.control}
            name="consentDataSharingLivliFamily"
            render={({ field }) => (
              <FormItem variant="portal" className="col-span-1 lg:col-span-2">
                <FormLabel className="!font-lively !text-base font-bold">
                  Adesão ao Programa de Vantagens Livli Family
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5 md:h-10">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentDataSharingLivliFamily"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    {CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormFooter>
          <Button
            variant="portal-destructive"
            disabled={updateCustomerMutation.isPending}
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="portal"
            disabled={
              !formUpdateCustomerInfo.formState.isDirty ||
              formUpdateCustomerInfo.formState.isSubmitting ||
              updateCustomerMutation.isPending
            }
            isLoading={
              formUpdateCustomerInfo.formState.isSubmitting ||
              updateCustomerMutation.isPending
            }
          >
            <SaveIcon />
            Guardar Alterações
          </Button>
        </FormFooter>
      </form>
    </Form>
  );
};

export default FormCustomerInfo;
